<script setup>
import { ref } from 'vue'
import { sendJson } from '../lib/csrf'

const props = defineProps({
  searchUrl: { type: String, default: '/fridge' },
})

const terms = ref([])
const input = ref('')
const state = ref('idle') // idle | searching | done | error
const results = ref([])

function addTerm() {
  const value = input.value.trim().toLowerCase()
  if (value && !terms.value.includes(value)) terms.value.push(value)
  input.value = ''
}

function removeTerm(term) {
  terms.value = terms.value.filter((t) => t !== term)
}

async function search() {
  addTerm()
  if (terms.value.length === 0) return
  state.value = 'searching'
  try {
    const data = await sendJson(props.searchUrl, { terms: terms.value })
    results.value = data.results
    state.value = 'done'
  } catch (e) {
    state.value = 'error'
  }
}
</script>

<template>
  <div>
    <div class="flex flex-wrap items-center gap-2 mb-4">
      <span v-for="term in terms" :key="term" class="badge badge-primary badge-lg gap-1">
        {{ term }}
        <button type="button" class="opacity-70 hover:opacity-100" @click="removeTerm(term)" :aria-label="`Remove ${term}`">✕</button>
      </span>
    </div>

    <form class="flex gap-2 mb-8" @submit.prevent="search">
      <input v-model="input" @keydown.enter.prevent="addTerm" @keydown.,.prevent="addTerm"
             class="input input-bordered flex-1" placeholder="chorizo, eggs, spinach… (enter adds each)" autofocus>
      <button type="button" class="btn" @click="addTerm">Add</button>
      <button type="submit" class="btn btn-primary" :disabled="state === 'searching'">
        {{ state === 'searching' ? 'Thinking…' : 'What can I cook?' }}
      </button>
    </form>

    <p v-if="state === 'error'" class="alert alert-error">Something went wrong — try again.</p>
    <p v-else-if="state === 'done' && results.length === 0" class="lede text-center py-8">
      Nothing leaps out — try fewer or different ingredients.
    </p>

    <ol v-else class="space-y-4">
      <li v-for="(r, i) in results" :key="r.recipe_id" class="card bg-base-100 border border-base-300">
        <div class="card-body py-4 flex-row items-start gap-4">
          <span class="text-3xl font-semibold tracking-tight text-primary/60 leading-none select-none">{{ i + 1 }}</span>
          <div class="flex-1">
            <a :href="`/recipes/${r.slug}`" class="recipe-title text-xl link-hover">{{ r.title }}</a>
            <p v-if="r.reason" class="text-[0.9375rem] leading-relaxed text-base-content/70 mt-1">{{ r.reason }}</p>
            <div class="flex flex-wrap gap-2 mt-2">
              <span v-if="r.category" class="badge badge-ghost badge-sm">{{ r.category }}</span>
              <span v-if="r.match_count" class="badge badge-outline badge-sm">
                uses {{ r.match_count }} of yours{{ r.matched_terms.length ? `: ${r.matched_terms.join(', ')}` : '' }}
              </span>
            </div>
          </div>
        </div>
      </li>
    </ol>
  </div>
</template>
